import type { SlateBlock } from '@atlas/renderer';
import type { KeyboardEvent } from 'react';
import { Editor, Transforms } from 'slate';
import areSamePoints from '@atlas/tool/box/slate/areSamePoints';
import getNode from '@atlas/tool/box/slate/getNode';
import isOneLineTextBlock from '@atlas/tool/box/slate/isOneLineTextBlock';

export default function handleMarkdownBlockFormattingKeyDownEvent(editor: Editor, event: KeyboardEvent): boolean {
    if (!editor.selection) return false;

    const { anchor, focus } = editor.selection;
    if (!areSamePoints(anchor, focus)) return false;

    if (event.altKey || event.metaKey || event.ctrlKey) {
        return false;
    }

    const blockIndex = focus.path[0];
    const block = getNode<SlateBlock>(editor, [blockIndex]);
    if (!block) return false;

    if (!isOneLineTextBlock(block)) return false;

    if (event.key === ' ') {
        const textBefore = Editor.string(editor, {
            anchor: { offset: 0, path: [blockIndex, 0] },
            focus,
        });

        switch (textBefore) {
            case '#':
                if (block.type === 'heading-one') return false;
                event.preventDefault();
                Editor.deleteBackward(editor, { unit: 'word' });
                Transforms.setNodes(editor, { type: 'heading-one' });
                return true;
            case '##':
                if (block.type === 'heading-two') return false;
                event.preventDefault();
                Editor.deleteBackward(editor, { unit: 'word' });
                Transforms.setNodes(editor, { type: 'heading-two' });
                return true;
            case '###':
                if (block.type === 'heading-three') return false;
                event.preventDefault();
                Editor.deleteBackward(editor, { unit: 'word' });
                Transforms.setNodes(editor, { type: 'heading-three' });
                return true;
            case '>':
                event.preventDefault();
                Editor.deleteBackward(editor, { unit: 'word' });
                Transforms.setNodes(editor, { type: 'block-quote' });
                return true;
            case '-':
            case '—':
            case '*':
                event.preventDefault();
                Editor.withoutNormalizing(editor, () => {
                    Editor.deleteBackward(editor, { unit: 'word' });
                    Transforms.setNodes(editor, { type: 'list-item' });
                    Transforms.wrapNodes(editor, {
                        type: 'bulleted-list',
                        children: [],
                    });
                });
                return true;
        }

        if (textBefore.match(/^\d+\.$/)) {
            const start = parseInt(textBefore);
            event.preventDefault();
            Editor.withoutNormalizing(editor, () => {
                Editor.deleteBackward(editor, { unit: 'word' });
                Transforms.setNodes(editor, { type: 'list-item' });
                Transforms.wrapNodes(editor, {
                    type: 'numbered-list',
                    children: [],
                    ...(start && start > 1 && { start }),
                });
            });
            return true;
        }

        return false;
    }

    if (event.key === '`') {
        const textBefore = Editor.string(editor, {
            anchor: { offset: 0, path: [blockIndex, 0] },
            focus,
        });

        if (textBefore !== '``') return false;

        event.preventDefault();

        Editor.deleteBackward(editor, { unit: 'word' });
        Transforms.setNodes(editor, { type: 'code-block' });

        return true;
    }

    if (event.key === '-') {
        const textBefore = Editor.string(editor, {
            anchor: { offset: 0, path: [blockIndex, 0] },
            focus,
        });

        if (textBefore !== '--') return false;

        const textAfter = Editor.string(editor, {
            anchor: Editor.end(editor, [blockIndex]),
            focus,
        });

        if (textAfter) return false;

        event.preventDefault();

        Editor.deleteBackward(editor, { unit: 'word' });
        Transforms.setNodes(editor, { type: 'horizontal-line' });

        return true;
    }

    return false;
}

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('handleMarkdownBlockFormattingKeyDownEvent', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
